import { useOutletContext } from "react-router";

import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";

import { ClientDetailResponse } from "@/api";
import { useTranslation } from "@/translations";

import { VolunteerAssignmentCard } from "./components/VolunteerAssignmentCard";
import { useVolunteerAssignment } from "./hooks/useVolunteerAssignment";

export function VolunteerAssignmentView() {
  const client = useOutletContext<ClientDetailResponse>();
  const { t } = useTranslation();
  const { assignment, isPending, isError, volunteerOptions, assign, revoke } = useVolunteerAssignment(client.clientId);

  const renderContent = () => {
    if (isPending) {
      return (
        <Stack alignItems="center" py={4}>
          <CircularProgress aria-label={t("volunteerAssignment.loading")} />
        </Stack>
      );
    }

    // A failed query settles with isPending false and no data — without this branch the loading
    // spinner would render forever.
    if (isError || !assignment) {
      return (
        <Alert severity="error" variant="outlined">
          {t("volunteerAssignment.loadError")}
        </Alert>
      );
    }

    return (
      <VolunteerAssignmentCard
        // Remount once the assignment actually changes, so the dropdown reseeds from the new state
        // instead of holding the selection the user just submitted.
        key={assignment.volunteerId ?? "unassigned"}
        assignment={assignment}
        volunteerOptions={volunteerOptions}
        onAssign={(volunteerId) => assign.mutate(volunteerId)}
        onRevoke={() => revoke.mutate()}
        isSubmitting={assign.isPending || revoke.isPending}
      />
    );
  };

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <Stack gap={2} bgcolor="background.paper" p={2} flexShrink={0}>
        <Box>
          <Typography variant="h5">{t("volunteerAssignment.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("volunteerAssignment.description")}
          </Typography>
        </Box>

        {renderContent()}
      </Stack>
    </Stack>
  );
}
